import React, { useEffect, useMemo, useState } from "react";
import {
  getButikkKapasiteter,
  getKapasitetsOverstyringer,
} from "../../lib/tjenestekatalogApi";
import {
  normalizeWorkshopSlug,
  isoDate,
  weekStartISO,
  getWeekNumber,
  fmtMinutes,
  getWeekdayNameNo,
  normalizeEnumDay,
  prettifyStoreName,
} from "./workshopCapacityUtils";

function buildDayCapacityRows(butikk, storeSlug, weeksAhead, overrides) {
  const rows = [];
  const today = new Date();
  const allowedDays = (butikk?.dagsbudsjettdager || []).map(normalizeEnumDay);

  for (let i = 0; i < weeksAhead * 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);

    const dateIso = isoDate(d);
    const weekdayEnum = normalizeEnumDay(
      ["sondag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lordag"][
        d.getDay()
      ]
    );

    if (!allowedDays.includes(weekdayEnum)) continue;

    const relevantOverrides = (overrides || []).filter(
      (o) =>
        normalizeWorkshopSlug(o?.lokasjon || "") ===
        normalizeWorkshopSlug(storeSlug)
    );

    const datoOverride = relevantOverrides.find((o) => o?.dato === dateIso);
    const ukeOverride = relevantOverrides.find(
      (o) => o?.uke && weekStartISO(o.uke) === weekStartISO(d)
    );

    let kapasitet = Number(butikk?.dagsbudsjett) || 0;
    if (!kapasitet && butikk?.ukebudsjett) {
      kapasitet = Math.floor(Number(butikk.ukebudsjett) / 5);
    }

    let source = "base";

    if (datoOverride?.stengt) {
      kapasitet = 0;
      source = "dato stengt";
    } else if (typeof datoOverride?.minutter === "number") {
      kapasitet += datoOverride.minutter;
      source = "dato override";
    } else if (ukeOverride?.stengt) {
      kapasitet = 0;
      source = "uke stengt";
    } else if (typeof ukeOverride?.minutter === "number") {
      kapasitet += Math.floor(ukeOverride.minutter / 5);
      source = "uke override";
    }

    rows.push({
      id: `day-${dateIso}`,
      dateIso,
      weekIso: weekStartISO(d),
      weekNo: getWeekNumber(d),
      weekday: getWeekdayNameNo(d),
      kapasitet: Math.max(0, kapasitet),
      source,
    });
  }

  return rows;
}

function buildWeekRows(butikk, storeSlug, weeksAhead, overrides) {
  const rows = [];
  const today = new Date();
  const baseWeek = Number(butikk?.ukebudsjett) || 0;
  const storeNorm = normalizeWorkshopSlug(storeSlug);

  for (let i = 0; i < weeksAhead; i++) {
    const d = new Date(today);
    const monday = new Date(weekStartISO(d));
    monday.setDate(monday.getDate() + i * 7);

    const weekIso = isoDate(monday);
    const fridayDate = new Date(monday);
    fridayDate.setDate(monday.getDate() + 4);
    const fridayIso = isoDate(fridayDate);

    const override = (overrides || []).find(
      (o) =>
        normalizeWorkshopSlug(o?.lokasjon || "") === storeNorm &&
        o?.uke &&
        weekStartISO(o.uke) === weekIso
    );

    let kapasitet = baseWeek;
    let source = "base";
    let closedDayISOs = [];
    let intakeDate = weekIso;
    let pickupDate = fridayIso;

    if (override?.stengt) {
      kapasitet = 0;
      source = "uke stengt";
    } else if (typeof override?.minutter === "number") {
      kapasitet += override.minutter;
      source = "uke override";
    }

    if (!override?.stengt) {
      for (let dayI = 0; dayI < 5; dayI++) {
        const dayDate = new Date(monday);
        dayDate.setDate(monday.getDate() + dayI);
        const dayIso = isoDate(dayDate);
        const datoOv = (overrides || []).find(
          (o) =>
            normalizeWorkshopSlug(o?.lokasjon || "") === storeNorm &&
            o?.dato === dayIso &&
            o?.stengt
        );
        if (datoOv) closedDayISOs.push(dayIso);
      }

      if (closedDayISOs.length > 0) {
        kapasitet = Math.floor(kapasitet * (5 - closedDayISOs.length) / 5);
        source = source === "base"
          ? `${closedDayISOs.length} dag(er) stengt`
          : `${source} + ${closedDayISOs.length} dag(er) stengt`;
      }

      if (closedDayISOs.includes(weekIso)) {
        const tuesday = new Date(monday);
        tuesday.setDate(monday.getDate() + 1);
        intakeDate = isoDate(tuesday);
      }

      if (closedDayISOs.includes(fridayIso)) {
        const thursday = new Date(fridayDate);
        thursday.setDate(fridayDate.getDate() - 1);
        pickupDate = isoDate(thursday);
      }
    }

    rows.push({
      id: `week-${weekIso}`,
      weekIso,
      weekNo: getWeekNumber(monday),
      year: monday.getFullYear(),
      kapasitet: Math.max(0, kapasitet),
      source,
      closedDayISOs,
      intakeDate,
      pickupDate,
      isMondayClosed: closedDayISOs.includes(weekIso),
      isFridayClosed: closedDayISOs.includes(fridayIso),
    });
  }

  return rows;
}

// Bygger rader for et 3-dagers delvindu av uken (tidliguke: man–ons,
// senuke: ons–fre) — samme prinsipp som buildWeekRows, men med eget
// budsjettfelt, eget override-felt og et 3-dagers vindu i stedet for 5.
function buildSubWeekRows({
  butikk,
  storeSlug,
  weeksAhead,
  overrides,
  budgetField,
  overrideField,
  startOffset,
  endOffset,
  label,
}) {
  const rows = [];
  const today = new Date();
  const baseBudget = Number(butikk?.[budgetField]) || 0;
  const storeNorm = normalizeWorkshopSlug(storeSlug);
  const windowDays = endOffset - startOffset + 1;

  for (let i = 0; i < weeksAhead; i++) {
    const d = new Date(today);
    const monday = new Date(weekStartISO(d));
    monday.setDate(monday.getDate() + i * 7);

    const weekIso = isoDate(monday);
    const startDate = new Date(monday);
    startDate.setDate(monday.getDate() + startOffset);
    const startIso = isoDate(startDate);
    const endDate = new Date(monday);
    endDate.setDate(monday.getDate() + endOffset);
    const endIso = isoDate(endDate);

    const override = (overrides || []).find(
      (o) =>
        normalizeWorkshopSlug(o?.lokasjon || "") === storeNorm &&
        o?.[overrideField] &&
        weekStartISO(o[overrideField]) === weekIso
    );

    let kapasitet = baseBudget;
    let source = "base";
    let closedDayISOs = [];
    let intakeDate = startIso;
    let pickupDate = endIso;

    if (override?.stengt) {
      kapasitet = 0;
      source = `${label} stengt`;
    } else if (typeof override?.minutter === "number") {
      kapasitet += override.minutter;
      source = `${label} override`;
    }

    if (!override?.stengt) {
      for (let dayI = startOffset; dayI <= endOffset; dayI++) {
        const dayDate = new Date(monday);
        dayDate.setDate(monday.getDate() + dayI);
        const dayIso = isoDate(dayDate);
        const datoOv = (overrides || []).find(
          (o) =>
            normalizeWorkshopSlug(o?.lokasjon || "") === storeNorm &&
            o?.dato === dayIso &&
            o?.stengt
        );
        if (datoOv) closedDayISOs.push(dayIso);
      }

      if (closedDayISOs.length > 0) {
        kapasitet = Math.floor(kapasitet * (windowDays - closedDayISOs.length) / windowDays);
        source = source === "base"
          ? `${closedDayISOs.length} dag(er) stengt`
          : `${source} + ${closedDayISOs.length} dag(er) stengt`;
      }

      if (closedDayISOs.includes(startIso)) {
        const nextDay = new Date(startDate);
        nextDay.setDate(startDate.getDate() + 1);
        intakeDate = isoDate(nextDay);
      }

      if (closedDayISOs.includes(endIso)) {
        const prevDay = new Date(endDate);
        prevDay.setDate(endDate.getDate() - 1);
        pickupDate = isoDate(prevDay);
      }
    }

    rows.push({
      id: `${label}-${weekIso}`,
      weekIso,
      weekNo: getWeekNumber(monday),
      year: monday.getFullYear(),
      kapasitet: Math.max(0, kapasitet),
      source,
      closedDayISOs,
      intakeDate,
      pickupDate,
      isStartClosed: closedDayISOs.includes(startIso),
      isEndClosed: closedDayISOs.includes(endIso),
    });
  }

  return rows;
}

function SectionTable({ title, columns, rows, emptyText = "Ingen data" }) {
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      <div className="px-4 py-3 border-b bg-gray-50 font-semibold">
        {title}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2 ${
                    col.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, idx) => (
                <tr key={row.id || `${title}-${idx}`} className="border-t">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-2 ${
                        col.align === "right" ? "text-right" : "text-left"
                      }`}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-6 text-center text-gray-500"
                >
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function WorkshopCapacityPlanTab({ storeSlug, weeksAhead }) {
  const [loading, setLoading] = useState(true);
  const [butikk, setButikk] = useState(null);
  const [overrides, setOverrides] = useState([]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);

      try {
        const [butikker, overrideRows] = await Promise.all([
          getButikkKapasiteter(),
          getKapasitetsOverstyringer(),
        ]);

        const normalizedStore = normalizeWorkshopSlug(storeSlug);

        const valgtButikk = (butikker || []).find(
          (b) => normalizeWorkshopSlug(b?.slug || "") === normalizedStore
        );

        console.log("storeSlug:", storeSlug);
        console.log("normalizedStore:", normalizedStore);
        console.log(
          "butikker fra hygraph:",
          (butikker || []).map((b) => ({
            slug: b?.slug,
            normalized: normalizeWorkshopSlug(b?.slug || ""),
            ukebudsjett: b?.ukebudsjett,
            lokasjonsNavn: b?.lokasjonsNavn,
          }))
        );
        console.log("valgtButikk:", valgtButikk);

        setButikk(valgtButikk || null);
        setOverrides(overrideRows || []);
      } catch (err) {
        console.error("Feil ved henting av planlagt kapasitet:", err);
        setButikk(null);
        setOverrides([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [storeSlug]);

  const weekRows = useMemo(() => {
    if (!butikk) return [];
    return buildWeekRows(butikk, storeSlug, weeksAhead, overrides);
  }, [butikk, storeSlug, weeksAhead, overrides]);

  const tidligUkeRows = useMemo(() => {
    if (!butikk) return [];
    return buildSubWeekRows({
      butikk,
      storeSlug,
      weeksAhead,
      overrides,
      budgetField: "tidligukebudsjett",
      overrideField: "tidliguke",
      startOffset: 0,
      endOffset: 2,
      label: "tidliguke",
    });
  }, [butikk, storeSlug, weeksAhead, overrides]);

  const senUkeRows = useMemo(() => {
    if (!butikk) return [];
    return buildSubWeekRows({
      butikk,
      storeSlug,
      weeksAhead,
      overrides,
      budgetField: "senukebudsjett",
      overrideField: "senuke",
      startOffset: 2,
      endOffset: 4,
      label: "senuke",
    });
  }, [butikk, storeSlug, weeksAhead, overrides]);

  const dayRows = useMemo(() => {
    if (!butikk) return [];
    return buildDayCapacityRows(butikk, storeSlug, weeksAhead, overrides);
  }, [butikk, storeSlug, weeksAhead, overrides]);

  if (loading) {
    return <div className="py-10 text-center">Laster planlagt kapasitet…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded border bg-blue-50 p-4">
        <div className="text-sm text-gray-600">Planlagt kapasitet for</div>
        <div className="text-xl font-bold">{prettifyStoreName(storeSlug)}</div>
        <div className="text-sm text-gray-700 mt-1">
          Viser kapasitet som er lagt inn under Tjenester og priser, fordelt på uke og dag.
        </div>
      </div>

      <SectionTable
        title="Uke-kapasitet"
        rows={weekRows}
        columns={[
          {
            key: "weekNo",
            label: "Uke",
            render: (r) => `Uke ${r.weekNo} / ${r.year}`,
          },
          { key: "weekIso", label: "Uke start" },
          {
            key: "kapasitet",
            label: "Kapasitet",
            align: "right",
            render: (r) => fmtMinutes(r.kapasitet),
          },
          {
            key: "intakeDate",
            label: "Innlevering",
            render: (r) =>
              r.isMondayClosed ? (
                <span className="text-amber-700 font-medium">{r.intakeDate} ⚠️ tirsdag</span>
              ) : (
                r.intakeDate
              ),
          },
          {
            key: "pickupDate",
            label: "Henting",
            render: (r) =>
              r.isFridayClosed ? (
                <span className="text-amber-700 font-medium">{r.pickupDate} ⚠️ torsdag</span>
              ) : (
                r.pickupDate
              ),
          },
          { key: "source", label: "Kilde" },
        ]}
      />

      <SectionTable
        title="Tidliguke-kapasitet"
        rows={tidligUkeRows}
        columns={[
          {
            key: "weekNo",
            label: "Uke",
            render: (r) => `Uke ${r.weekNo} / ${r.year}`,
          },
          { key: "weekIso", label: "Uke start" },
          {
            key: "kapasitet",
            label: "Kapasitet",
            align: "right",
            render: (r) => fmtMinutes(r.kapasitet),
          },
          {
            key: "intakeDate",
            label: "Innlevering",
            render: (r) =>
              r.isStartClosed ? (
                <span className="text-amber-700 font-medium">{r.intakeDate} ⚠️ tirsdag</span>
              ) : (
                r.intakeDate
              ),
          },
          {
            key: "pickupDate",
            label: "Henting",
            render: (r) =>
              r.isEndClosed ? (
                <span className="text-amber-700 font-medium">{r.pickupDate} ⚠️ tirsdag</span>
              ) : (
                r.pickupDate
              ),
          },
          { key: "source", label: "Kilde" },
        ]}
      />

      <SectionTable
        title="Senuke-kapasitet"
        rows={senUkeRows}
        columns={[
          {
            key: "weekNo",
            label: "Uke",
            render: (r) => `Uke ${r.weekNo} / ${r.year}`,
          },
          { key: "weekIso", label: "Uke start" },
          {
            key: "kapasitet",
            label: "Kapasitet",
            align: "right",
            render: (r) => fmtMinutes(r.kapasitet),
          },
          {
            key: "intakeDate",
            label: "Innlevering",
            render: (r) =>
              r.isStartClosed ? (
                <span className="text-amber-700 font-medium">{r.intakeDate} ⚠️ torsdag</span>
              ) : (
                r.intakeDate
              ),
          },
          {
            key: "pickupDate",
            label: "Henting",
            render: (r) =>
              r.isEndClosed ? (
                <span className="text-amber-700 font-medium">{r.pickupDate} ⚠️ torsdag</span>
              ) : (
                r.pickupDate
              ),
          },
          { key: "source", label: "Kilde" },
        ]}
      />

      <SectionTable
        title="Dag-kapasitet"
        rows={dayRows}
        columns={[
          { key: "dateIso", label: "Dato" },
          { key: "weekday", label: "Dag" },
          {
            key: "weekNo",
            label: "Uke",
            render: (r) => `Uke ${r.weekNo}`,
          },
          {
            key: "kapasitet",
            label: "Kapasitet",
            align: "right",
            render: (r) => fmtMinutes(r.kapasitet),
          },
          { key: "source", label: "Kilde" },
        ]}
      />
    </div>
  );
}